import Place, { Place_Source } from "./Place";

export default class WikiPlaces {
  constructor() {
    this.places = [];
  }

  // https://en.wikipedia.org/w/api.php?action=query&list=geosearch&gscoord=48.7891838|2.4033522&gsradius=10000&format=json&gslimit=50

  // https://en.wikipedia.org/w/api.php?action=query&prop=extracts|pageimages|coordinates&format=json&exintro=1&pithumbsize=250&titles=Uffizi

  // https://en.wikipedia.org/?curid=18630637

  searchByText = (query, limit, callback) => {
    const url = `https://en.wikipedia.org/w/api.php?action=query&list=search&srsearch=${encodeURIComponent(query)}&srlimit=${limit}&format=json&origin=*`;
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        if (!data.query || !data.query.search || data.query.search.length === 0) {
          callback(null);
          return;
        }
        const pageids = data.query.search.map((p) => p.pageid).join("|");
        const detailUrl = `https://en.wikipedia.org/w/api.php?action=query&prop=extracts|pageimages|coordinates&format=json&origin=*&exintro=1&exlimit=1&exchars=200&pithumbsize=250&pageids=${pageids}`;
        fetch(detailUrl)
          .then((r) => r.json())
          .then((details) => callback(details))
          .catch(() => callback(null));
      })
      .catch(() => callback(null));
  };

  // Wikipedia articles as places, WITH or WITHOUT a map location
  // (2026-10-17, Create Places from Photos → "Wikipedia articles"): a topic
  // ("fighter aircraft", "lighthouses of Maine") rather than places on a
  // map. Each place gets the article's title, opening text, a "See more"
  // link and its lead photo — screen size for the app, the original for
  // printing, with its licence and author (Common/photoCredit.js). Lead
  // images that aren't free (Wikipedia's own "fair use" files: logos,
  // posters) are left off. Resolves to an array of Places (may be empty),
  // or null on a network error.
  searchArticles = async (query, limit = 20) => {
    const api = "https://en.wikipedia.org/w/api.php?format=json&origin=*";
    try {
      const found = await (await fetch(`${api}&action=query&list=search&srsearch=${encodeURIComponent(query)}&srlimit=${limit}&srnamespace=0`)).json();
      const hits = found?.query?.search || [];
      if (hits.length === 0) return [];
      const order = hits.map((h) => h.pageid);
      // 20 pages per request: Wikipedia returns intro text for at most 20.
      const pages = {};
      for (let i = 0; i < order.length; i += 20) {
        const details = await (await fetch(
          `${api}&action=query&prop=extracts|pageimages|coordinates&exintro=1&explaintext=1&exsentences=3&exlimit=max&piprop=thumbnail|original|name&pithumbsize=800&pilimit=max&pageids=${order.slice(i, i + 20).join("|")}`
        )).json();
        Object.assign(pages, details?.query?.pages || {});
      }
      // The lead images' licences, in one request. "shared" = Wikimedia
      // Commons (always a free licence); "local" = a file kept on Wikipedia
      // itself, which is how non-free (fair use) images are stored.
      const files = [...new Set(Object.values(pages).map((p) => p.pageimage).filter(Boolean))];
      const fileInfo = {};
      for (let i = 0; i < files.length; i += 50) {
        const batch = files.slice(i, i + 50).map((f) => `File:${f}`).join("|");
        const info = await (await fetch(
          `${api}&action=query&prop=imageinfo&iiprop=url|size|extmetadata&iiextmetadatafilter=LicenseShortName|Artist&titles=${encodeURIComponent(batch)}`
        )).json();
        const norm = {};
        (info?.query?.normalized || []).forEach((n) => { norm[n.to] = n.from; });
        Object.values(info?.query?.pages || {}).forEach((fp) => {
          const ii = fp.imageinfo && fp.imageinfo[0];
          if (!ii) return;
          const title = (norm[fp.title] || fp.title || "").replace(/^File:/, "");
          fileInfo[title.replace(/ /g, "_")] = { ii, shared: fp.imagerepository === "shared" };
        });
      }
      const plain = (html) => String(html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
      const results = [];
      order.forEach((id) => {
        const page = pages[id];
        if (!page || /\(disambiguation\)$/i.test(page.title || "")) return;
        const place = new Place();
        place.createIDFromPlacesArray(results);
        place.Name = page.title || "";
        const coord = page.coordinates && page.coordinates[0];
        if (coord) { place.Lat = coord.lat; place.Longi = coord.lon; }
        const url_link = `https://en.m.wikipedia.org/?curid=${page.pageid}`;
        place.Hlink = `wikipedia|${url_link}`;
        const file = page.pageimage ? fileInfo[String(page.pageimage).replace(/ /g, "_")] : null;
        const free = file && file.shared;
        if (free && page.thumbnail?.source) {
          place.Photo = page.thumbnail.source;
          place.Photo_m = page.thumbnail.source;
          if (page.original?.source && page.original.source !== page.thumbnail.source) place.PhotoPrint = page.original.source;
          // Size for the picker's low-resolution warning — not saved with
          // the place (non-enumerable, so it's left out of the guide data).
          if (file.ii.width) Object.defineProperty(place, "_printSize", { value: { width: file.ii.width, height: file.ii.height }, enumerable: false });
          const meta = file.ii.extmetadata || {};
          place.PhotoCredit = {
            author: plain(meta.Artist && meta.Artist.value).replace(/^Creator:\s*/i, ""),
            license: (meta.LicenseShortName && meta.LicenseShortName.value) || "",
            source: "Wikimedia Commons",
            pageUrl: file.ii.descriptionurl || "",
          };
        }
        const text = (page.extract || "").trim();
        place.Info = `${place.Photo_m ? `<p style="text-align:center;"><img src="${place.Photo_m}" alt="Image" style="width:100%; max-width:700px; height:auto; border-radius:10%" data-photo="${place.Photo}" onerror="window.imgFallback(this)"></img></p>` : ""}<p>${text.replace(/&/g, "&amp;").replace(/</g, "&lt;")}</p><a href="urllink:${url_link}"> ... See More</a>`;
        place.PType = "Icon";
        place.Points = null;
        place.Source = Place_Source.WIKIPEDIA;
        results.push(place);
      });
      return results;
    } catch (e) {
      console.log("Wikipedia searchArticles", e?.message);
      return null;
    }
  };

  parseTextSearchPlaces = (places) => {
    const results = [];
    if (!places || !places.query || !places.query.pages) return results;
    const pages = places.query.pages;
    Object.values(pages).forEach((page) => {
      if (!page.coordinates || page.coordinates.length === 0) return;
      if (!page.thumbnail) return;
      const lat = page.coordinates[0].lat;
      const lon = page.coordinates[0].lon;
      const new_place = new Place();
      new_place.createIDFromPlacesArray(results);
      new_place.Name = page.title || "";
      new_place.Lat = lat;
      new_place.Longi = lon;
      if (page.extract) new_place.Info = page.extract.replace("...", "");
      if (page.thumbnail && page.thumbnail.source) {
        new_place.Photo = page.thumbnail.source;
        new_place.Photo_m = page.thumbnail.source;
      }
      const url_link = `https://en.m.wikipedia.org/?curid=${page.pageid}`;
      new_place.Hlink = `wikipedia|${url_link}`;
      if (new_place.Photo_m)
        new_place.Info = `<p style="text-align:center;"><img src="${new_place.Photo_m}" alt="Image" style="width:100%; max-width:700px; height:auto; border-radius:10%" data-photo="${new_place.Photo}" onerror="window.imgFallback(this)"></img></p>${new_place.Info}<a href="urllink:${url_link}"> ... See More</a>`;
      new_place.PType = "Icon";
      new_place.Points = null;
      new_place.Source = Place_Source.WIKIPEDIA;
      results.push(new_place);
    });
    return results;
  };

  searchPlaces = (bbox, limit, callback) => {
    const url = `https://en.wikipedia.org/w/api.php?action=query&list=geosearch&format=json&origin=*&gsbbox=${encodeURIComponent(bbox)}&gslimit=${limit}`;
    console.log("Wikipedia searchPlaces", url);
    fetch(url)
      .then(function (response) { return response.json(); })
      .then(function (response) { callback(response); })
      .catch(function (error) {
        console.log("Error in Wikipedia search", error);
        callback(null);
      });
  };
  res = {
    batchcomplete: "",
    query: {
      geosearch: [
        {
          pageid: 53992389,
          ns: 0,
          title: "Cantons of Vitry-sur-Seine",
          lat: 48.79,
          lon: 2.4,
          dist: 261.8,
          primary: "",
        },
        {
          pageid: 458871,
          ns: 0,
          title: "Vitry-sur-Seine",
          lat: 48.7875,
          lon: 2.3928,
          dist: 795.4,
          primary: "",
        },
      ],
    },
  };
  _last = 0;
  _index = 0;
  _places = [];
  _results = [];
  _callback = null;

  res2 = {
    batchcomplete: "",
    warnings: {
      extracts: {
        "*": "HTML may be malformed and/or unbalanced and may omit inline images. Use at your own risk. Known problems are listed at https://www.mediawiki.org/wiki/Special:MyLanguage/Extension:TextExtracts#Caveats.",
      },
    },
    query: {
      pages: {
        458871: {
          pageid: 458871,
          ns: 0,
          title: "Vitry-sur-Seine",
          extract:
            '<p><b>Vitry-sur-Seine</b> (<small>French pronunciation: ​</small><span title="Representation in the International Phonetic Alphabet (IPA)">[vi.tʁi.syʁ.sɛn]</span>) is a commune in the southeastern suburbs of Paris, France. It is located 7.5 km (4.7 mi) from the centre of Paris.\n</p>',
          thumbnail: {
            source:
              "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c1/Vitry_Hotel_de_Ville_DSCN1793.JPG/250px-Vitry_Hotel_de_Ville_DSCN1793.JPG",
            width: 250,
            height: 188,
          },
          pageimage: "Vitry_Hotel_de_Ville_DSCN1793.JPG",
          coordinates: [
            {
              lat: 48.7875,
              lon: 2.3928,
              primary: "",
              globe: "earth",
            },
          ],
        },
        53992389: {
          pageid: 53992389,
          ns: 0,
          title: "Cantons of Vitry-sur-Seine",
          extract:
            "<p>The <b>cantons of Vitry-sur-Seine</b> are administrative divisions of the Val-de-Marne department, Île-de-France region, northern France. Since the French canton reorganisation which came into effect in March 2015, the city of Vitry-sur-Seine is subdivided into 2 cantons. Their seat is in Vitry-sur-Seine.</p>",
          coordinates: [
            {
              lat: 48.79,
              lon: 2.4,
              primary: "",
              globe: "earth",
            },
          ],
        },
      },
    },
  };
  // https://en.wikipedia.org/w/api.php?action=query&prop=extracts|pageimages|coordinates&format=json&exintro=1&pithumbsize=250&pageids=53992389|458871

  getDetails = (places, callback) => {
    console.log("Wikipedia getDetails", places);
    if (places === null) {
      console.log("Wikipedia GetDetails, places === null");
    }
    let pageids = "";
    places.forEach((place) => {
      pageids += pageids === "" ? place.pageid : `|${place.pageid}`;
    });

    const url = `https://en.wikipedia.org/w/api.php?action=query&prop=extracts|pageimages|coordinates&format=json&origin=*&exintro=1&exlimit=1&exchars=200&pithumbsize=250&pageids=${pageids}`;
    console.log("getDetails", url);
    fetch(url)
      .then(function (response) {
        return response.json();
      })
      .then(function (response) {
        //console.log("response", JSON.stringify(response));
        callback(response);
      })
      .catch(function (error) {
        console.log("Wikipedia error in GetDetails", error);
        callback(null);
      });
  };

  parsePlaces = (wikipedia_geosearch_results, places, searchText) => {
    //console.log("searchText", searchText);
    let results = [];
    if (places == null) {
      return results;
    }
    if (typeof places["query"] == "undefined") {
      return results;
    }
    if (typeof places["query"]["pages"] == "undefined") {
      return results;
    }
    let details = places["query"]["pages"];
    //console.log("parsePlaces", details);
    const keys = Object.keys(details);
    //console.log("keys", keys);
    keys.forEach((key) => {
      let geoplace = wikipedia_geosearch_results.find((x) => x.pageid == key);
      if (geoplace == null || geoplace == "undefined") return;
      if (typeof details[key].thumbnail == "undefined") return;
      let title = "";
      let extract = "";
      if (typeof details[key].extract != "undefined") extract = details[key].extract;
      if (typeof details[key].title != "undefined") title = details[key].title;
      if (searchText != "")
        if (
          !title.toUpperCase().includes(searchText.toUpperCase()) &&
          !extract.toUpperCase().includes(searchText.toUpperCase())
        )
          return;
      let new_place = new Place();
      new_place.createIDFromPlacesArray(results);
      if (typeof title != "undefined") new_place.Name = title;
      if (typeof extract != "undefined") new_place.Info = extract;
      //if (details[key].title == "Île Saint-Germain")//console.log("Île Saint-Germain", details);
      if (isNaN(geoplace.lat || isNaN(geoplace.lon))) return;
      if (typeof geoplace.lat != "undefined") new_place.Lat = geoplace.lat;
      if (typeof geoplace.lon != "undefined") new_place.Longi = geoplace.lon;
      if (typeof details[key].extract != "undefined") new_place.Info = details[key].extract.replace("...", "");
      if (typeof details[key].thumbnail != "undefined") {
        console.log("details[key])", details[key]);
        if (typeof details[key].thumbnail.source != "undefined") {
          new_place.Photo = details[key].thumbnail.source;
          new_place.Photo_m = details[key].thumbnail.source;
        }
      }
      new_place.Hlink = `wikipedia|https://en.m.wikipedia.org/?curid=${key}`;
      const url_link = `https://en.m.wikipedia.org/?curid=${key}`;
      if (new_place.Photo_m !== "")
        new_place.Info = `<p style="text-align:center;"><img src="${new_place.Photo_m}" alt="Image" style="width:100%; max-width:700px; height:auto; border-radius:10%" data-photo="${new_place.Photo}" onerror="window.imgFallback(this)"></img></p>${new_place.Info}<a href="urllink:${url_link}"> ... See More</a>`;
      new_place.PType = "Icon";
      new_place.Points = null;
      new_place.Source = Place_Source.WIKIPEDIA;
      results.push(new_place);
    });
    //console.log(results);
    return results;
  };

  moveToGuide = (guide, place) => {
    guide.places.addPlace(place);
  };
}
